import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, CalendarDays, ChevronDown } from "lucide-react";
import { IMAGES } from "../../data/content";
import { MaskedLine } from "../shared/Reveal";
import { PrimaryButton, GhostButton } from "../shared/Buttons";

export const Hero = () => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 240]);
  const bgScale = useTransform(scrollY, [0, 900], [1, 1.12]);
  const fade = useTransform(scrollY, [0, 550], [1, 0]);

  return (
    <section
      data-testid="hero-section"
      className="relative isolate flex min-h-[100svh] flex-col justify-end overflow-hidden"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 -z-10">
        <img
          src={IMAGES.hero}
          alt="Martial arts arena under lights"
          className="h-[115%] w-full object-cover object-[center_75%] opacity-[0.6]"
          loading="eager"
        />
      </motion.div>
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-ink/60 via-ink/30 to-ink" />
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(900px_circle_at_75%_20%,rgba(220,38,38,0.22),transparent_60%)]" />
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(600px_circle_at_15%_85%,rgba(212,175,55,0.08),transparent_60%)]" />

      <motion.div style={{ opacity: fade }} className="mx-auto w-full max-w-7xl px-5 pb-16 pt-40 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-8 flex flex-wrap items-center gap-4"
        >
          <span
            data-testid="hero-aotearoa-badge"
            className="inline-flex items-center gap-2 border border-gold/30 bg-gold/[0.06] px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-gold"
          >
            Aotearoa • New Zealand
          </span>
          <span
            data-testid="hero-stats-pill"
            className="inline-flex items-center gap-2 border border-white/10 bg-white/[0.03] px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-slate-300"
          >
            1 Event • 5 Disciplines • 1 Community
          </span>
        </motion.div>

        <h1 className="font-display uppercase leading-[0.85] tracking-tight text-bone">
          <MaskedLine delay={0.35} className="text-[19vw] sm:text-[15vw] lg:text-[11rem]">
            Kumite
          </MaskedLine>
          <MaskedLine delay={0.5} className="text-[19vw] sm:text-[15vw] lg:text-[11rem]">
            <span className="text-kred">Games</span>
            <span className="text-gold">.</span>
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-tagline"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-8 max-w-xl font-head text-lg font-semibold text-slate-200 sm:text-xl"
        >
          One Event. Many Disciplines. One Martial Arts Community.
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base"
        >
          Kumite Games brings martial arts organisations, athletes, coaches, families and
          communities together for a major celebration of martial arts in New Zealand.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <PrimaryButton to="/event" testId="hero-explore-button">
            Explore Kumite Games
          </PrimaryButton>
          <GhostButton to="/disciplines" testId="hero-disciplines-button">
            View Martial Arts
          </GhostButton>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.35 }}
          className="mt-14 grid max-w-xl grid-cols-2 divide-x divide-white/10 border-y border-white/10"
        >
          <div className="py-6 pr-6">
            <p className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-kred">
              <CalendarDays className="h-3.5 w-3.5" /> Event Date
            </p>
            <p data-testid="hero-event-date" className="mt-3 font-display text-3xl uppercase tracking-wide text-bone sm:text-4xl">
              Coming Soon
            </p>
          </div>
          <div className="py-6 pl-6">
            <p className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-kred">
              <MapPin className="h-3.5 w-3.5" /> Location
            </p>
            <p data-testid="hero-location" className="mt-3 font-display text-3xl uppercase tracking-wide text-bone sm:text-4xl">
              New Zealand
            </p>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 sm:block"
        aria-hidden="true"
      >
        <ChevronDown className="h-5 w-5 animate-bounce text-slate-500" />
      </motion.div>
    </section>
  );
};
